import { motion } from "framer-motion";
import { Check, X } from "lucide-react";

const STEPS = ["Confirm", "Details", "Done"];

export const StepDots = ({ step }) => (
  <ol className="flex items-center gap-2" data-testid="register-steps-dots">
    {STEPS.map((s, i) => {
      const n = i + 1;
      const state = n < step ? "done" : n === step ? "now" : "next";
      return (
        <li key={s} className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest ${state === "now" ? "bg-white text-[#1A2E26]" : state === "done" ? "bg-emerald-400/90 text-[#0F1B16]" : "bg-white/15 text-white/70"}`}>
            <span className={`grid place-items-center w-4 h-4 rounded-full text-[9px] ${state === "now" ? "bg-[#C86A51] text-white" : state === "done" ? "bg-[#0F1B16]/20" : "bg-white/20"}`}>{state === "done" ? <Check size={9} /> : n}</span>{s}
          </span>
          {i < STEPS.length - 1 && <span className={`h-px w-4 ${n < step ? "bg-emerald-300" : "bg-white/25"}`} />}
        </li>
      );
    })}
  </ol>
);

export const ColorHeader = ({ kicker, title, sub, step, onClose, closeTestId, tone = "green", icon }) => {
  const bg = tone === "amber" ? "linear-gradient(135deg,#8A5A12 0%,#B57E17 55%,#D9A441 100%)" : tone === "rose" ? "linear-gradient(135deg,#7A2E3A 0%,#A64B57 60%,#C86A51 100%)" : "linear-gradient(135deg,#0F1B16 0%,#1A2E26 55%,#2F6B4F 100%)";
  return (
    <div className="relative px-6 sm:px-8 pt-6 pb-7 text-white overflow-hidden" style={{ background: bg }}>
      <div className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 rounded-full bg-white/10 blur-2xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-10 w-44 h-44 rounded-full bg-[#C86A51]/30 blur-2xl" />
      <button type="button" data-testid={closeTestId} onClick={onClose} className="absolute top-4 right-4 grid place-items-center w-9 h-9 rounded-full bg-white/10 border border-white/20 hover:bg-white/20 transition-colors z-10"><X size={16} /></button>
      {step && <div className="mb-4">{<StepDots step={step} />}</div>}
      <div className="flex items-start gap-3">
        {icon && <span className="grid place-items-center w-11 h-11 rounded-2xl bg-white/15 border border-white/20 shrink-0">{icon}</span>}
        <div className="min-w-0">
          {kicker && <p className="text-[10px] uppercase tracking-[0.3em] text-[#F3C9A8] mb-1.5">{kicker}</p>}
          <h2 className="font-serif text-2xl sm:text-3xl leading-tight">{title}</h2>
          {sub && <p className="text-xs sm:text-sm text-white/75 mt-1.5 leading-relaxed">{sub}</p>}
        </div>
      </div>
    </div>
  );
};

export const ModalFrame = ({ children, testId, onClose, zIndex = 85, maxW = "max-w-md" }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 grid place-items-center p-3 sm:p-6" style={{ zIndex }} onClick={onClose}>
    <div className="absolute inset-0 bg-[#0F1B16]/50 backdrop-blur-sm" />
    <motion.div
      initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.98 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }} onClick={(e) => e.stopPropagation()} data-testid={testId}
      className={`relative bg-[#FBFAF6] rounded-3xl shadow-[0_30px_80px_rgba(15,27,22,0.35)] w-full ${maxW} max-h-[calc(100dvh-1.5rem)] sm:max-h-[90vh] overflow-y-auto overflow-x-hidden`}
    >
      {children}
    </motion.div>
  </motion.div>
);

export const Field = ({ label, icon, hint, right, children, testId, tone = "green" }) => (
  <div data-testid={testId}>
    <div className="flex items-center justify-between gap-3 mb-1.5">
      <label className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest" style={{ color: tone === "amber" ? "#8A5A12" : "#2F6B4F" }}>{icon}{label}</label>
      {right}
    </div>
    {children}
    {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
  </div>
);

export const inputCls = "w-full rounded-xl border border-[#1A2E26]/15 bg-white px-3.5 py-2.5 text-sm outline-none transition-[border-color,box-shadow] focus:border-[#2F6B4F] focus:shadow-[0_0_0_3px_rgba(47,107,79,0.15)] placeholder:text-muted-foreground/50 disabled:opacity-60 disabled:cursor-not-allowed";
export const readonlyCls = "w-full rounded-xl border border-[#C86A51]/30 bg-[#FFF4EC] px-3.5 py-2.5 text-sm font-semibold text-[#8C3F2A] outline-none";
export const primaryBtn = "w-full group inline-flex items-center justify-center gap-2 rounded-full px-8 py-3.5 text-sm font-semibold text-white bg-gradient-to-r from-[#C86A51] to-[#E08A5B] hover:from-[#B85C45] hover:to-[#D47C4C] shadow-[0_10px_30px_rgba(200,106,81,0.35)] hover:-translate-y-0.5 transition-[transform,box-shadow,background-color] duration-300 disabled:opacity-60 disabled:hover:translate-y-0";
export const ghostBtn = "inline-flex items-center justify-center rounded-full border border-[#1A2E26]/20 bg-white px-6 py-3 text-sm text-[#1A2E26] hover:bg-[#F4F1EA] transition-colors";
